import { useEffect, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { MobButton, MobSplash } from '../../components/mob';
import { outletService } from '../../api/services/outletService';
import { tokenStore } from '../../auth/tokenStore';
import { authEvents } from '../../auth/authEvents';
import { useSession } from '../../auth/useSession';
import type { OutletBriefDto } from '../../api/types/dine.types';
import { OUTLET_NAME_KEY } from '../layouts/AppShell';
import './LoginScreen.css';

/**
 * Every cached query (card, tables, orders, tickets, the outlet itself) belongs
 * to one outlet. Cleared whenever an outlet is chosen, so the picker never
 * shows the last outlet's card and prices while the order line is priced for
 * this one.
 */
function remember(o: OutletBriefDto, qc: QueryClient) {
  qc.clear();
  tokenStore.setOutletId(o.id);
  tokenStore.setSection(null); // sections belong to the outlet: choose again
  try {
    localStorage.setItem(OUTLET_NAME_KEY, o.outletName);
  } catch {
    // storage unavailable: the shell reads the name from /outlet anyway
  }
}

/** No outlet (none assigned, the list failed, or the stored one is no longer allowed): nothing cached may survive. */
function forget(qc: QueryClient) {
  qc.clear();
  tokenStore.setOutletId(null);
  tokenStore.setSection(null);
}

/** Only an active outlet can be worked in. */
function workable(list: OutletBriefDto[]): OutletBriefDto[] {
  return list.filter((o) => o.isActive);
}

function errMsg(e: unknown): string {
  return e instanceof Error && e.message ? e.message : 'Could not load your outlets';
}

type GateState = 'checking' | 'choose' | 'none' | 'error' | 'ready';

/**
 * Multi-outlet (plan O9). Sits between sign-in and the app: a waiter who may
 * work in several outlets picks one before any screen loads; with one outlet it
 * is chosen without asking; with none, or when the list cannot be read, the app
 * does not load. The choice lives in the tab's session and goes on every call
 * as X-Outlet-ID; signing out clears it. A stored choice is re-checked against
 * /outlets/mine, and a call the API refuses for its outlet (the axios client
 * emits 'outletCleared') brings the waiter back here.
 */
export default function OutletGate() {
  const [state, setState] = useState<GateState>(tokenStore.getOutletId() ? 'ready' : 'checking');
  const [outlets, setOutlets] = useState<OutletBriefDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const { logout } = useSession();

  // Resolve the outlet: choose, auto-pick, none, or error.
  useEffect(() => {
    if (state !== 'checking') return;
    let live = true;
    outletService
      .mine()
      .then((all) => {
        if (!live) return;
        const list = workable(all);
        if (list.length > 1) {
          forget(qc);
          setOutlets(list);
          setState('choose');
          return;
        }
        if (list.length === 1) {
          remember(list[0], qc);
          setState('ready');
          return;
        }
        forget(qc);
        setState('none');
      })
      .catch((e: unknown) => {
        if (!live) return;
        forget(qc);
        setError(errMsg(e));
        setState('error');
      });
    return () => {
      live = false;
    };
  }, [state, qc]);

  // A stored outlet (F5, or a tab restored) is re-checked in the background:
  // dropped and asked again when it is no longer this waiter's or no longer
  // active. A failed check keeps it; the API still refuses a foreign outlet.
  useEffect(() => {
    const stored = tokenStore.getOutletId();
    if (!stored) return;
    let live = true;
    outletService
      .mine()
      .then((all) => {
        if (!live || tokenStore.getOutletId() !== stored) return;
        if (!workable(all).some((o) => o.id === stored)) {
          forget(qc);
          setState('checking');
        }
      })
      .catch(() => {
        // offline or the API is down: keep working; refusals come back per call
      });
    return () => {
      live = false;
    };
    // Once per mount: later changes go through the resolve effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any call refused for its outlet: the client already dropped it; ask again.
  useEffect(() => {
    return authEvents.on('outletCleared', () => {
      qc.clear();
      setState('checking');
    });
  }, [qc]);

  if (state === 'checking') return <MobSplash />;

  if (state === 'none') {
    return (
      <GateFrame label="No outlet assigned" data="data-outlet-none">
        <div className="wtr-grp" style={{ marginTop: 0 }}>
          No outlet is assigned to you
        </div>
        <div className="login__sub" style={{ marginBottom: 12 }}>
          Ask your manager to give you access to an outlet, then sign in again.
        </div>
        <MobButton variant="secondary" fullWidth onClick={() => void logout()}>
          Sign out
        </MobButton>
      </GateFrame>
    );
  }

  if (state === 'error') {
    return (
      <GateFrame label="Could not load your outlets" data="data-outlet-error">
        <div className="login__error" role="alert">
          {error ?? 'Could not load your outlets'}
        </div>
        <MobButton fullWidth onClick={() => setState('checking')}>
          Retry
        </MobButton>
        <MobButton variant="secondary" fullWidth onClick={() => void logout()}>
          Sign out
        </MobButton>
      </GateFrame>
    );
  }

  if (state === 'choose') {
    return (
      <GateFrame label="Choose your outlet" data="data-outlet-picker">
        <div className="wtr-grp" style={{ marginTop: 0 }}>
          Choose your outlet
        </div>
        {outlets.map((o) => (
          <button
            key={o.id}
            type="button"
            className="wtr-opt"
            onClick={() => {
              remember(o, qc);
              setState('ready');
            }}
          >
            <span className="wtr-opt__nm">
              {o.outletName}
              <span className="login__picker-app"> · {o.outletCode}{o.outletType === 'ROOM_SERVICE' ? ' · room service' : ''}</span>
            </span>
          </button>
        ))}
        <div className="login__sub" style={{ marginTop: 8 }}>
          To change outlet later, sign out and sign in again.
        </div>
      </GateFrame>
    );
  }

  return <Outlet />;
}

function GateFrame({ label, data, children }: { label: string; data: string; children: ReactNode }) {
  return (
    <div className="login wtr-body">
      <div className="login__col">
        <div className="login__brand">
          <div className="login__name">DineOps</div>
          <div className="login__sub">Waiter</div>
        </div>
        <div className="login__picker" role="region" aria-label={label} {...{ [data]: '' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
